import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

const BCMInstructionPage = () => {
  const navigate = useNavigate();
  const [stakeholderName, setStakeholderName] = useState('');
  const [stakeholderEmail, setStakeholderEmail] = useState('');
  const [stakeholderDepartment, setStakeholderDepartment] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem("access_token");

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    axios.get(`${process.env.REACT_APP_API_URL}users/user/profile/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      const { name, email, department } = res.data;
      setStakeholderName(name || '');
      setStakeholderEmail(email || '');
      setStakeholderDepartment(department || 'N/A');
    })
    .catch((err) => {
      console.error("Failed to load profile info", err);
      setError("Unable to load user profile. Please login again.");
    });
  }, [navigate, token]);

  const handleSubmitDashboard = (event) => {
    event.preventDefault();
    if (!stakeholderName) { setError("User profile not loaded. Please refresh the page."); return; }
    if (!token) { setError("No authentication token found. Please login."); return; }
    setError('');
    navigate('/bcm/dashboard');
  };

  const handleSubmitAssessment = (event) => {
    event.preventDefault();
    if (!stakeholderName) { setError("User profile not loaded. Please refresh the page."); return; }
    if (!token) { setError("No authentication token found. Please login."); return; }
    setError('');
    navigate('/bcm/assessment');
  };

  return (
    <div className="min-h-screen flex flex-col bg-amber-50 text-gray-800">
      <Helmet>
        <title>Instructions | BCM</title>
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-10">
        <div className="bg-white shadow-xl rounded-2xl p-8 border-t-8 border-amber-600">
          <h2 className="text-3xl font-bold text-amber-700 mb-6">
            📋 Instructions for Completing the BCM Assessment
          </h2>

          {/* Stakeholder Details */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold text-amber-800 mb-2">Participating Stakeholder Details</h3>
            <p className="text-gray-600 mb-4">Your details have been loaded from your profile:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input type="text" value={stakeholderName} readOnly className="p-3 border rounded-lg bg-gray-100" aria-label="Stakeholder Name" />
              <input type="email" value={stakeholderEmail} readOnly className="p-3 border rounded-lg bg-gray-100" aria-label="Stakeholder Email" />
              <input type="text" value={stakeholderDepartment} readOnly className="p-3 border rounded-lg bg-gray-100" aria-label="Stakeholder Department" />
            </div>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </section>

          <hr className="my-8 border-amber-200" />

          {/* Introduction */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold text-amber-800">Introduction</h3>
            <p className="text-gray-600 mt-2">
              Welcome to the <span className="font-semibold text-amber-700">Business Continuity Management (BCM)</span> Assessment. This assessment evaluates the maturity of your organization's business continuity processes and preparedness.
            </p>
            <p className="text-gray-600 mt-2">
              The results will help identify gaps, assess readiness, and guide improvements for ensuring resilient operations under disruptions.
            </p>
            <p className="text-gray-700 font-medium mt-4">
              The assessment focuses on the following BCM Phases:
            </p>
            <ul className="list-disc list-inside mt-2 text-amber-700 space-y-1">
              <li>Establishing a BCMS</li>
              <li>Embracing Business Continuity</li>
              <li>Analysis</li>
              <li>Solutions Design</li>
              <li>Enabling Solutions</li>
              <li>Validation</li>
            </ul>
          </section>

          <hr className="my-8 border-amber-200" />

          {/* Instructions */}
          <section className="mb-8">
            <h3 className="text-xl font-semibold text-amber-800">How to Complete the Questionnaire</h3>
            <ul className="list-disc list-inside mt-4 text-gray-700 space-y-2">
              <li><strong className="text-amber-700">BCM Phase:</strong> The phase of business continuity under review.</li>
              <li><strong className="text-amber-700">Description:</strong> Explanation of the processes or controls being assessed.</li>
              <li><strong className="text-amber-700">Response:</strong> Answer "Yes" or "No".</li>
              <li><strong className="text-amber-700">Evidence:</strong> Attach supporting documents, reports, or records.</li>
            </ul>
          </section>

          <hr className="my-8 border-amber-200" />

          {/* Additional Notes */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-amber-800">Additional Notes</h3>
            <ul className="list-disc list-inside mt-4 text-gray-700 space-y-2">
              <li>Base your responses on actual practices, not planned actions.</li>
              <li>Consult colleagues if unsure about any question.</li>
              <li>The goal is to assess real operational resilience today.</li>
            </ul>
          </section>

          {/* Action Buttons */}
          <div className="flex justify-center gap-6">
            <button
              onClick={handleSubmitDashboard}
              className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-full font-semibold shadow-md"
            >
              📊 View BCM Dashboard
            </button>
            <button
              onClick={handleSubmitAssessment}
              className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-full font-semibold shadow-md"
            >
              📝 Start BCM Assessment
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BCMInstructionPage;
